import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Paper,
  Typography
} from '@mui/material'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import PhoneOutlined from '@mui/icons-material/PhoneOutlined'
import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import { formatEntityAddress } from '../../v2/components/ExecutiveEntityQuickView'
import DashboardWidgets from '../dashboard-v1/DashboardWidgets'

const ExecutiveEntityLocations = ({ locations = [] }) => {
  const navigate = useNavigate()
  const [expandedLocations, setExpandedLocations] = useState({})

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => (
      (a.name || '').localeCompare(b.name || '')
    )),
    [locations]
  )

  const toggleLocation = (locationId) => (_, expanded) => {
    setExpandedLocations(previous => ({
      ...previous,
      [locationId]: expanded
    }))
  }

  const openLocationDetails = (locationId) => {
    window.localStorage.setItem('locId', locationId)
    window.localStorage.setItem('locIdForMap', locationId)
    navigate(`/tab/tab0/${locationId}`)
  }

  if (!sortedLocations.length) {
    return (
      <Paper className="executive-entity-locations__empty" variant="outlined">
        <Typography variant="h6">No locations found</Typography>
        <Typography>This entity does not have any accessible locations.</Typography>
      </Paper>
    )
  }

  return (
    <Box className="executive-entity-locations">
      {sortedLocations.map(location => {
        const address = formatEntityAddress(location.address)
        const coordinates = location.address?.lat != null && location.address?.long != null
          ? `${location.address.lat},${location.address.long}`
          : null

        return (
          <Accordion
            key={location.id}
            expanded={Boolean(expandedLocations[location.id])}
            onChange={toggleLocation(location.id)}
            disableGutters
            elevation={0}
            className="executive-entity-locations__item"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`location-${location.id}-content`}
              id={`location-${location.id}-header`}
            >
              <Typography className="executive-entity-locations__name">
                {location.name || 'Unnamed location'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              id={`location-${location.id}-content`}
              className="executive-entity-locations__details"
            >
              <Box className="executive-entity-locations__contact">
                {coordinates ? (
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    <PlaceOutlined /> {address}
                  </Link>
                ) : (
                  <span><PlaceOutlined /> {address}</span>
                )}
                {location.phone ? (
                  <Link href={`tel:${location.phone}`} underline="hover">
                    <PhoneOutlined /> {location.phone}
                  </Link>
                ) : (
                  <span><PhoneOutlined /> Phone not available</span>
                )}
              </Box>

              <Button
                type="button"
                variant="outlined"
                endIcon={<ChevronRight />}
                onClick={() => openLocationDetails(location.id)}
                className="executive-entity-locations__view-button"
              >
                View location details
              </Button>

              <Box className="executive-entity-locations__performance">
                {expandedLocations[location.id] ? (
                  <DashboardWidgets
                    className="executive-entity-locations__widgets"
                    locationId={location.id}
                    header
                    compact
                  />
                ) : null}
              </Box>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

ExecutiveEntityLocations.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.object
  }))
}

export default ExecutiveEntityLocations
